import type { ResumeDocument } from "@/types/cv"

const ORGANIZATION_KEYWORD_PATTERNS: RegExp[] = [
  // English keywords
  /\bvolunteer(?:ing)?\b/i,
  /\bvoluntary\b/i,
  /\bcommittee\b/i,
  /\bcouncil\b/i,
  /\bclub\b/i,
  /\bchapter\b/i,
  /\bassociation\b/i,
  /\bsociety\b/i,
  /\bcommunity\b/i,
  /\bcommunity service\b/i,
  /\borganization\b/i,
  /\borg\b/i,
  /\bngo\b/i,
  /\bnon[\s-]?governmental(?: organization)?\b/i,
  /\bnon[\s-]?profit\b/i,
  /\bcharity\b/i,
  /\bfoundation\b/i,
  /\bstudent council\b/i,
  /\bstudent organization\b/i,
  /\bstudent senate\b/i,
  /\bexecutive board\b/i,
  /\bboard member\b/i,
  /\balumni(?: association)?\b/i,
  /\bfraternity\b/i,
  /\bsorority\b/i,

  // Indonesian keywords
  /\b(?:relawan|sukarelawan)\b/i,
  /\b(?:panitia|kepanitiaan)\b/i,
  /\bkomite\b/i,
  /\bkomunitas\b/i,
  /\borganisasi\b/i,
  /\basosiasi\b/i,
  /\bhimpunan\b/i,
  /\bpaguyuban\b/i,
  /\byayasan\b/i,
  /\bnirlaba\b/i,
  /\blsm\b/i,
  /\blembaga swadaya masyarakat\b/i,
  /\bormawa\b/i,
  /\b(?:bem|badan eksekutif mahasiswa)\b/i,
  /\bsenat mahasiswa\b/i,
  /\bdewan mahasiswa\b/i,
  /\b(?:osis|karang taruna)\b/i,
  /\b(?:unit kegiatan mahasiswa|ukm mahasiswa)\b/i,
  /\bbakti sosial\b/i,
  /\bpengabdian masyarakat\b/i,
]

type ExperienceEntry = ResumeDocument["working_experience"][number]
type OrganizationEntry = ResumeDocument["organizations"][number]

const isOrganizationLikeExperience = (entry: ExperienceEntry): boolean => {
  const searchableText = [
    entry.title,
    entry.rewrite_title,
    entry.company,
    entry.experience_detail,
  ].join(" ")

  return ORGANIZATION_KEYWORD_PATTERNS.some((pattern) =>
    pattern.test(searchableText),
  )
}

const mapExperienceToOrganization = (
  entry: ExperienceEntry,
): OrganizationEntry => ({
  title: entry.title,
  rewrite_title: entry.rewrite_title,
  org: entry.company,
  location: entry.location,
  start_date: entry.start_date,
  end_date: entry.end_date,
  is_current: entry.is_current,
  experience_detail: entry.experience_detail,
  points: [...entry.points],
})

export const normalizeRewriteResumeSections = (
  resume: ResumeDocument | null,
): ResumeDocument | null => {
  if (!resume) return null

  if (resume.organizations.length > 0 || resume.working_experience.length === 0) {
    return resume
  }

  const organizationsFromExperience: OrganizationEntry[] = []
  const retainedWorkingExperience: ExperienceEntry[] = []

  resume.working_experience.forEach((entry) => {
    if (isOrganizationLikeExperience(entry)) {
      organizationsFromExperience.push(mapExperienceToOrganization(entry))
      return
    }

    retainedWorkingExperience.push(entry)
  })

  if (organizationsFromExperience.length === 0) {
    return resume
  }

  return {
    ...resume,
    working_experience: retainedWorkingExperience,
    organizations: organizationsFromExperience,
  }
}
